import React, { useMemo } from 'react';

import { Grid, Icon, Stack, Tag, Text } from '@eluve/components';
import { type FhirPageControls, LoadMoreRow } from '@eluve/fhir-data';
import {
  FhirResourceScope,
  RenderResource,
  buildResolutionMap,
  observationDateText,
} from '@eluve/fhir-renderer';

import {
  type GroupedObservations,
  type ObservationGroup,
  firstPendingGroup,
  formatMeasureValue,
  pendingReviewCount,
} from '../measures';

import { TrendSparkline } from './TrendSparkline';

/**
 * The Overview-tab Observations card (ENG-5366): catalog measures first —
 * grouped rows with latest/first values and a sparkline — then the
 * non-catalog observations as a reverse-chronological list rendered by the
 * FHIR renderer. All interactivity (row click targets, review pill) is app
 * chrome around renderer primitives, per the renderer's presentational
 * contract.
 */
export type ObservationsCardProps = {
  grouped: GroupedObservations;
  loading: boolean;
  failed: boolean;
  paging: FhirPageControls;
  /** Open the detail drawer for a group (routing owned by the section). */
  onOpenGroup: (token: string) => void;
};

const CenteredNote: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Grid place="center" className="px-6 py-12">
    <Text size="sm" color="tertiary" weight="regular">
      {children}
    </Text>
  </Grid>
);

const PendingDot: React.FC = () => (
  <span
    aria-hidden
    className="size-1.75 rounded-full border-[1.5px] border-border-insights-subtle bg-bg-primary"
    style={{ borderColor: 'var(--color-text-insights)' }}
  />
);

const MeasureRow: React.FC<{
  group: ObservationGroup;
  onOpen: () => void;
}> = ({ group, onOpen }) => {
  const hasPending = group.pending.length > 0;
  const latestPending = hasPending
    ? group.pending[group.pending.length - 1]
    : undefined;
  const hasAmended = group.series.some((point) => point.amended);
  const first = group.series[0];
  const last = group.series[group.series.length - 1];
  const recordedDate = observationDateText(last?.observation ?? group.latest);

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`measure-row-${group.measure?.key ?? group.token}`}
      className="flex w-full items-center justify-between gap-4 px-4 py-2.5 text-left not-first:border-t not-first:border-border-primary hover:bg-bg-secondary"
    >
      <div className="min-w-0">
        <Stack direction="horizontal" gap={2} align="center" fullWidth={false}>
          <Text inline size="sm" weight="medium" className="truncate">
            {group.label}
          </Text>
          {hasPending && (
            <Tag pill subtle size="xs" color="insights" label="Draft" />
          )}
          {!hasPending && hasAmended && (
            <Tag pill subtle size="xs" color="warning" label="Amended" />
          )}
        </Stack>
        <Text size="xs" color="tertiary" weight="regular" className="mt-0.5">
          {[
            recordedDate ? `Recorded ${recordedDate}` : undefined,
            group.method ? `Method: ${group.method}` : undefined,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Text>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        {first && last && group.series.length > 1 ? (
          <div className="grid grid-cols-[auto_1.5rem_auto] items-baseline text-sm tabular-nums">
            <Text inline size="sm" color="tertiary" className="text-center">
              {formatMeasureValue(first.value, group.unit)}
            </Text>
            <span className="text-center text-text-tertiary opacity-60">→</span>
            <Text inline size="sm" weight="semibold" className="text-center">
              {formatMeasureValue(last.value, group.unit)}
            </Text>
          </div>
        ) : last ? (
          <Text inline size="sm" weight="semibold" className="tabular-nums">
            {formatMeasureValue(last.value, group.unit)}
          </Text>
        ) : null}
        <TrendSparkline
          className="text-text-positive"
          points={group.series.map((point) => ({
            value: point.value,
            amended: point.amended,
          }))}
          proposedValue={latestPending?.valueQuantity?.value ?? undefined}
        />
      </div>
    </button>
  );
};

const OtherObservationRows: React.FC<{
  groups: ObservationGroup[];
  onOpenGroup: (token: string) => void;
}> = ({ groups, onOpenGroup }) => {
  const latestResources = useMemo(
    () => groups.map((group) => group.latest),
    [groups],
  );
  const map = useMemo(
    () => buildResolutionMap(latestResources),
    [latestResources],
  );
  return (
    <FhirResourceScope map={map}>
      <div data-testid="other-observations-list">
        {groups.map((group) => (
          <button
            key={group.token}
            type="button"
            onClick={() => onOpenGroup(group.token)}
            data-testid="other-observation-row"
            className="flex w-full items-center gap-3 px-4 text-left not-first:border-t not-first:border-border-primary hover:bg-bg-secondary"
          >
            <div className="min-w-0 flex-1">
              <RenderResource resource={group.latest} inList />
            </div>
            {group.pending.length > 0 && (
              <Tag pill subtle size="xs" color="insights" label="Draft" />
            )}
            {group.observations.length > 1 && (
              <Text inline size="xs" color="tertiary" weight="regular">
                {group.observations.length} values
              </Text>
            )}
            <Icon
              name="ChevronRight"
              size="xs"
              className="shrink-0 text-text-tertiary"
            />
          </button>
        ))}
      </div>
    </FhirResourceScope>
  );
};

export const ObservationsCard: React.FC<ObservationsCardProps> = ({
  grouped,
  loading,
  failed,
  paging,
  onOpenGroup,
}) => {
  const pendingCount = pendingReviewCount(grouped);
  const firstPending = firstPendingGroup(grouped);
  const isEmpty = grouped.measures.length === 0 && grouped.others.length === 0;

  return (
    <section
      data-testid="observations-card"
      className="rounded-lg border border-border-primary bg-bg-primary"
    >
      <Stack
        direction="horizontal"
        gap={2}
        align="center"
        justify="between"
        className="h-12 px-4"
      >
        <Text inline size="sm" weight="semibold">
          Observations
        </Text>
        {pendingCount > 0 && firstPending && (
          <button
            type="button"
            onClick={() => onOpenGroup(firstPending.token)}
            data-testid="observations-review-pill"
            className="flex items-center gap-1.5 rounded-full border border-border-primary px-2.5 py-0.5 text-xs font-semibold hover:bg-bg-secondary"
          >
            <PendingDot />
            {pendingCount} to review
          </button>
        )}
      </Stack>

      {failed ? (
        <CenteredNote>Couldn't load observations.</CenteredNote>
      ) : loading ? (
        <CenteredNote>Loading observations…</CenteredNote>
      ) : isEmpty ? (
        <CenteredNote>No observations recorded yet.</CenteredNote>
      ) : (
        <>
          {grouped.measures.length > 0 && (
            <div className="border-t border-border-primary">
              {grouped.measures.map((group) => (
                <MeasureRow
                  key={group.token}
                  group={group}
                  onOpen={() => onOpenGroup(group.token)}
                />
              ))}
            </div>
          )}
          {grouped.others.length > 0 && (
            <div className="border-t border-border-primary">
              <Text
                size="xs"
                color="tertiary"
                weight="medium"
                className="px-4 pt-3 pb-1 tracking-wide uppercase"
              >
                Other observations
              </Text>
              <OtherObservationRows
                groups={grouped.others}
                onOpenGroup={onOpenGroup}
              />
            </div>
          )}
          <LoadMoreRow paging={paging} noun="observations" />
        </>
      )}
    </section>
  );
};
